import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { AcademicianStartupButton } from "@/components/academician/academicianstartup-button";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MessageSquareIcon,
  UploadIcon,
  DownloadIcon,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type ContactSource =
  | "whatsapp"
  | "linkedin"
  | "referral"
  | "cold_outreach"
  | "event"
  | "other";
export type ContactStatus =
  | "to_contact"
  | "contacted"
  | "scheduled"
  | "completed"
  | "not_interested"
  | "no_response";

export interface Contact {
  id: string;
  name: string;
  segment: string;
  source: ContactSource;
  status: ContactStatus;
  email?: string;
  phone?: string;
  interviewDate?: string;
}

export interface SegmentOption {
  id: string;
  name: string;
}

export interface ContactsTabProps {
  contacts?: Contact[];
  segments?: SegmentOption[];
  onAdd?: (contact: Omit<Contact, "id">) => void;
  onEdit?: (id: string, contact: Omit<Contact, "id">) => void;
  onDelete?: (id: string) => void;
  onLogInterview?: (contactId: string) => void;
  bulkImportPath?: string;
  importTemplatePath?: string;
  csrfToken?: string;
  className?: string;
}

const sourceLabels: Record<ContactSource, string> = {
  whatsapp: "WhatsApp",
  linkedin: "LinkedIn",
  referral: "Referral",
  cold_outreach: "Cold Outreach",
  event: "Event",
  other: "Other",
};

const statusConfig: Record<ContactStatus, { label: string; color: string }> = {
  to_contact: {
    label: "To Contact",
    color: "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300",
  },
  contacted: {
    label: "Contacted",
    color: "bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary",
  },
  scheduled: {
    label: "Scheduled",
    color:
      "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400",
  },
  completed: {
    label: "Interviewed",
    color:
      "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400",
  },
  not_interested: {
    label: "Not Interested",
    color: "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400",
  },
  no_response: {
    label: "No Response",
    color: "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400",
  },
};

export function ContactsTab({
  contacts = [],
  segments = [],
  onAdd,
  onEdit,
  onDelete,
  onLogInterview,
  bulkImportPath,
  importTemplatePath,
  csrfToken,
  className,
}: ContactsTabProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<{ imported_count?: number; errors?: string[]; total_rows?: number } | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    segment: "",
    source: "linkedin" as ContactSource,
    status: "to_contact" as ContactStatus,
    interviewDate: "",
  });

  const handleOpenDialog = (contact?: Contact) => {
    if (contact) {
      setEditingId(contact.id);
      setFormData({
        name: contact.name,
        segment: contact.segment,
        source: contact.source,
        status: contact.status,
        interviewDate: contact.interviewDate || "",
      });
    } else {
      setEditingId(null);
      setFormData({
        name: "",
        segment: "",
        source: "linkedin",
        status: "to_contact",
        interviewDate: "",
      });
    }
    setIsDialogOpen(true);
  };

  const handleSave = () => {
    if (editingId) {
      onEdit?.(editingId, formData);
    } else {
      onAdd?.(formData);
    }
    setIsDialogOpen(false);
  };

  const handleImportSubmit = async () => {
    if (!importFile || !bulkImportPath || !csrfToken) return;
    setIsImporting(true);
    setImportResult(null);
    const fd = new FormData();
    fd.append("file", importFile);
    try {
      const response = await fetch(bulkImportPath, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken, Accept: "application/json" },
        body: fd,
      });
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { errors: [`Server error (${response.status}). Please try again or use a smaller file.`] };
      }
      setImportResult(data);
      if (data.imported_count > 0) {
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch {
      setImportResult({ errors: ["Network error. Please check your connection and try again."] });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className={className}>
      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mb-4">
        {bulkImportPath && (
          <AcademicianStartupButton
            variant="secondary"
            leftIcon={<UploadIcon />}
            onClick={() => { setIsImportDialogOpen(true); setImportFile(null); setImportResult(null); }}
          >
            Import CSV
          </AcademicianStartupButton>
        )}
        <AcademicianStartupButton
          variant="primary"
          leftIcon={<PlusIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Contact
        </AcademicianStartupButton>
      </div>

      {/* Table */}
      <p className="text-sm font-semibold text-primary italic mb-1">For Customer Discovery and Validation Purposes</p>
      <div className="text-xs text-muted-foreground mb-3 bg-muted/50 rounded-md px-3 py-2">
        <span className="font-medium">Note:</span> To bulk import contacts: 1) Download the .csv template. You can find the link to download the .csv template after you clicked "Import CSV" button. 2) Populate the .csv template with your contacts. 3) Mandatory column in the .csv file to upload: <span className="font-medium">Name</span>. 4) Other columns are optional, but it is best to include either phone or email information in that .csv file before uploading.
      </div>
      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[14%]">Name</TableHead>
              <TableHead className="w-[13%]">Customer</TableHead>
              <TableHead className="w-[14%]">Email</TableHead>
              <TableHead className="w-[11%]">Phone</TableHead>
              <TableHead className="w-[11%]">Source</TableHead>
              <TableHead className="w-[11%]">Status</TableHead>
              <TableHead className="w-[12%]">Interview Date</TableHead>
              <TableHead className="w-[14%] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contacts.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center py-8 text-muted-foreground"
                >
                  No contacts yet. Click "Add Contact" to get started.
                </TableCell>
              </TableRow>
            ) : (
              contacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell className="font-medium">{contact.name}</TableCell>
                  <TableCell>{contact.segment}</TableCell>
                  <TableCell className="text-sm text-muted-foreground truncate max-w-[150px]" title={contact.email || ""}>{contact.email || "-"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{contact.phone || "-"}</TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {sourceLabels[contact.source] || contact.source}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={statusConfig[contact.status]?.color || ""}>
                      {statusConfig[contact.status]?.label || contact.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {contact.interviewDate || "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {contact.status === "scheduled" && (
                        <AcademicianStartupButton
                          variant="primary"
                          size="sm"
                          leftIcon={<MessageSquareIcon />}
                          onClick={() => onLogInterview?.(contact.id)}
                        >
                          Log Interview
                        </AcademicianStartupButton>
                      )}
                      <AcademicianStartupButton
                        variant="ghost"
                        size="sm"
                        leftIcon={<PencilIcon />}
                        onClick={() => handleOpenDialog(contact)}
                        className="px-2"
                      />

                      <AcademicianStartupButton
                        variant="ghost"
                        size="sm"
                        leftIcon={<TrashIcon />}
                        onClick={() => onDelete?.(contact.id)}
                        className="px-2 text-destructive hover:text-destructive"
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Edit Contact" : "Add Contact"}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? "Update the contact details below."
                : "Add a new contact to track."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Contact name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="segment">Customer</Label>
              {segments.length > 0 ? (
                <Select
                  value={formData.segment}
                  onValueChange={(value) =>
                    setFormData({ ...formData, segment: value })
                  }
                >
                  <SelectTrigger id="segment">
                    <SelectValue placeholder="Select a customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {segments.map((seg) => (
                      <SelectItem key={seg.id} value={seg.name}>
                        {seg.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="segment"
                  value={formData.segment}
                  onChange={(e) =>
                    setFormData({ ...formData, segment: e.target.value })
                  }
                  placeholder="Add customers in the Customers tab first"
                />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="source">Source</Label>
              <Select
                value={formData.source}
                onValueChange={(value) =>
                  setFormData({ ...formData, source: value as ContactSource })
                }
              >
                <SelectTrigger id="source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="referral">Referral</SelectItem>
                  <SelectItem value="cold_outreach">Cold Outreach</SelectItem>
                  <SelectItem value="event">Event</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) =>
                  setFormData({ ...formData, status: value as ContactStatus })
                }
              >
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="to_contact">To Contact</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="completed">Interviewed</SelectItem>
                  <SelectItem value="not_interested">Not Interested</SelectItem>
                  <SelectItem value="no_response">No Response</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(formData.status === "scheduled" ||
              formData.status === "completed") && (
              <div className="space-y-2">
                <Label htmlFor="interviewDate">Interview Date</Label>
                <Input
                  id="interviewDate"
                  type="date"
                  value={formData.interviewDate}
                  onChange={(e) =>
                    setFormData({ ...formData, interviewDate: e.target.value })
                  }
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <AcademicianStartupButton
              variant="secondary"
              onClick={() => setIsDialogOpen(false)}
            >
              Cancel
            </AcademicianStartupButton>
            <AcademicianStartupButton
              variant="primary"
              onClick={handleSave}
              disabled={!formData.name || !formData.segment}
            >
              {editingId ? "Save Changes" : "Add Contact"}
            </AcademicianStartupButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Contacts from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file with contact data. Maximum {1000} rows per import.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {importTemplatePath && (
              <a
                href={importTemplatePath}
                className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 font-medium"
              >
                <DownloadIcon className="w-4 h-4" />
                Download template CSV
              </a>
            )}

            <div className="space-y-2">
              <Label htmlFor="csv-file">CSV File</Label>
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              />
            </div>

            {importResult && (
              <div className={`p-3 rounded-lg text-sm ${importResult.errors && importResult.errors.length > 0 ? 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400' : 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400'}`}>
                {importResult.imported_count !== undefined && importResult.imported_count > 0 && (
                  <p>Successfully imported {importResult.imported_count} of {importResult.total_rows} contacts. Reloading...</p>
                )}
                {importResult.errors && importResult.errors.length > 0 && (
                  <div>
                    <p className="font-medium mb-1">Errors:</p>
                    <ul className="list-disc pl-4 space-y-0.5">
                      {importResult.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                      {importResult.errors.length > 5 && (
                        <li>...and {importResult.errors.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <AcademicianStartupButton
              variant="secondary"
              onClick={() => setIsImportDialogOpen(false)}
            >
              Cancel
            </AcademicianStartupButton>
            <AcademicianStartupButton
              variant="primary"
              onClick={handleImportSubmit}
              disabled={!importFile || isImporting}
            >
              {isImporting ? "Importing..." : "Import"}
            </AcademicianStartupButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
